import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teacherService } from '../services';
import toast from 'react-hot-toast';

export function useDashboard() {
  return useQuery({
    queryKey: ['teacher', 'dashboard'],
    queryFn: () => teacherService.getDashboard(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useStudents() {
  return useQuery({
    queryKey: ['teacher', 'students'],
    queryFn: () => teacherService.getStudents(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

export function useStudent(studentId) {
  return useQuery({
    queryKey: ['teacher', 'student', studentId],
    queryFn: () => teacherService.getStudent(studentId),
    enabled: !!studentId,
  });
}

export function useStudentProgress(studentId) {
  return useQuery({
    queryKey: ['teacher', 'student', studentId, 'progress'],
    queryFn: () => teacherService.getStudentProgress(studentId),
    enabled: !!studentId,
  });
}

export function useCreateStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => teacherService.createStudent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'students'] });
      queryClient.invalidateQueries({ queryKey: ['teacher', 'dashboard'] });
      toast.success('Student added successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    },
  });
}

export function useUpdateStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, data }) =>
      teacherService.updateStudent(studentId, data),
    onSuccess: (_, { studentId }) => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'students'] });
      queryClient.invalidateQueries({ queryKey: ['teacher', 'student', studentId] });
      toast.success('Student updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update student');
    },
  });
}

export function useDeleteStudent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (studentId) => teacherService.deleteStudent(studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'students'] });
      queryClient.invalidateQueries({ queryKey: ['teacher', 'dashboard'] });
      toast.success('Student removed');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to remove student');
    },
  });
}

export function useRecentActivity(limit = 10) {
  return useQuery({
    queryKey: ['teacher', 'activity', limit],
    queryFn: () => teacherService.getRecentActivity(limit),
    staleTime: 1 * 60 * 1000,
  });
}

export function useClassStats() {
  return useQuery({
    queryKey: ['teacher', 'stats'],
    queryFn: () => teacherService.getClassStats(),
    staleTime: 2 * 60 * 1000,
  });
}

export function useProgressOverview() {
  return useQuery({
    queryKey: ['teacher', 'progress'],
    queryFn: () => teacherService.getProgressOverview(),
    staleTime: 1 * 60 * 1000,
  });
}

export function useCertificates() {
  return useQuery({
    queryKey: ['teacher', 'certificates'],
    queryFn: () => teacherService.getCertificates(),
    staleTime: 1 * 60 * 1000,
  });
}

export function useCertificateEligibility() {
  return useQuery({
    queryKey: ['teacher', 'certificates', 'eligibility'],
    queryFn: () => teacherService.getCertificateEligibility(),
    staleTime: 1 * 60 * 1000,
  });
}

export function useIssueCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => teacherService.issueCertificate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'certificates'] });
      toast.success('Certificate issued successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to issue certificate');
    },
  });
}

export default {
  useDashboard,
  useStudents,
  useStudent,
  useStudentProgress,
  useCreateStudent,
  useUpdateStudent,
  useDeleteStudent,
  useRecentActivity,
  useClassStats,
  useProgressOverview,
  useCertificates,
  useCertificateEligibility,
  useIssueCertificate,
};
